export type PaymentMethod = 'invoice' | 'card' | null;

export type UpdateProfileErrorField = 'username' | 'email' | 'password';
type SubscriptionVersion = 1 | 2 | 3;

export type UpdateProfileError =
  | 'missing'
  | 'too-short'
  | 'too-long'
  | 'invalid'
  | 'in-use'
  | 'bad-characters';

export type UpdateProfileResponse = {
  errors?: Record<UpdateProfileErrorField, UpdateProfileError>;
};

export function getUpdateProfileErrorCaption(value: UpdateProfileError) {
  switch (value) {
    case 'missing':
      return 'missing.';
    case 'in-use':
      return 'is already in use.';
    case 'too-long':
      return 'is too long.';
    case 'too-short':
      return 'is too short.';
    case 'invalid':
      return 'is invalid.';
    case 'bad-characters':
      return 'has invalid characters.';
    default:
      return 'is invalid.';
  }
}
export type SeatAllocations = {
  available: {
    paid: number;
  };
  used: {
    paid: number;
    viewer: number;
    free: number;
  };
};

export type Billing = {
  data: {
    brand: string | null | undefined;
    last4: string | null | undefined;
    expMonth: number | null;
    expYear: number | null;
    nextDue: string;
    isCanceled: boolean;
    plan: string | null;
    cycle: string | null;
    next: {
      plan: string;
      cycle: string;
    } | null;
    balance: number;
    nextBill: number;
    status: string;
    paymentMethod: PaymentMethod;
    subscriptionVersion: SubscriptionVersion;
    seats: SeatAllocations | null;
  };
};

export type Receipts = {
  businessName?: string | null;
  taxId?: string | null;
  businessAddress?: string | null;
  receipts?: Charge[];
};

export type BusinessDetails = {
  details: {
    address: Address | null;
    name: string | null;
    taxId: TaxId | null;
  };
};

type TaxId = {
  type: string | null; // ae_trn, au_abn, au_arn, bg_uic, br_cnpj, br_cpf, ca_bn, ca_gst_hst, ca_pst_bc, ca_pst_mb, ca_pst_sk, ca_qst, ch_vat, cl_tin, es_cif, eu_oss_vat, eu_vat, gb_vat, ge_vat, hk_br, hu_tin, id_npwp, il_vat, in_gst, is_vat, jp_cn, jp_rn, kr_brn, li_uid, mx_rfc, my_frp, my_itn, my_sst, no_vat, nz_gst, ru_inn, ru_kpp, sa_vat, sg_gst, sg_uen, si_tin, th_vat, tw_vat, ua_vat, us_ein, or za_vat. Note that some legacy tax IDs have type unknown
  value: string | null;
};

type Address = {
  city: string | null; // - City, district, suburb, town, or village.
  country: string | null; // -  Two-letter country code (ISO 3166-1 alpha-2).
  line1: string | null; // - Address line 1 (e.g., street, PO Box, or company name).
  line2: string | null; // - Address line 2 (e.g., apartment, suite, unit, or building).
  postalCode: string | null; // - ZIP or postal code.
  state: string | null; // - State, county, province, or region.
};

type Charge = {
  chargeId: number;
  created: string;
  amount: number | null;
  successful: number;
  receipt: string;
  relatedLineItemCodes: string[];
  description: string | null;
  isSubscription: boolean | null;
};

export type User = {
  id?: number | null;
  name?: string | null;
  username?: string | null;
  avatar?: string | null;
  email?: string | null;
};

export type IdentityProvider = {
  identifier: string;
  externalId: string;
  forceProviderLogin: boolean;
  autoAssignTeamOwnerId: number | null;
  identityProviderId: number;
  requiresIdentityProviderLogin: RequireSSOState;
  domains: string[];
  type: string;
  samlConfig?: SAMLConfig;
};

export type SAMLConfig = {
  ip: SAMLIdentityProvider;
  sp: SAMLServiceProvider;
};

export type SAMLIdentityProvider = {
  entityId: string;
  singleSignOnUrl: string;
  signingCertificate: string;
};

export type SAMLServiceProvider = {
  entityId: string;
  singleSignOnUrl: string;
};

export const requireSSOStates = ['no', 'linked', 'all'] as const;
export type RequireSSOState = typeof requireSSOStates[number];
